import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import logo from "../assets/images/logo.svg";
import faqimage from "../assets/images/faq.svg";
import partnerbg from "../assets/images/partner.jpg";

import superior from "../assets/images/cards.png";
import fastDelivery from "../assets/images/time.png";
import reliability from "../assets/images/navigator.png";
import reliability1 from "../assets/images/customer-service.png";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { AddAgencyInq } from "../API/ContactAPI";
import { getAllFAQ } from "../API/FAQApi";
import { PuffLoader } from "react-spinners";

// ✅ Features
const features = [
  { icon: superior, alt: "Attract-new-customers", title: "Earn Up to $150/month" },
  { icon: fastDelivery, alt: "Doorstep-delivery-convenience", title: "No fixed hours" },
  { icon: reliability, alt: "Onboarding-support", title: "Work in your area" },
  { icon: reliability1, alt: "Onboarding-support", title: "Onboard within 24 hrs" },
];

// ✅ Validation Schemas
const ShortFormSchema = Yup.object().shape({
  Name: Yup.string().required("Name is required"),
  Phone: Yup.string()
    .matches(/^[0-9]+$/, "Phone must be only digits")
    .required("Phone number is required"),
});

const AgencySchema = Yup.object().shape({
  CompanyName: Yup.string().required("Company Name is required"),
  Name: Yup.string().required("Name is required"),
  Email: Yup.string().email("Invalid email").required("Email is required"),
  Phone: Yup.string()
    .matches(/^[0-9]+$/, "Phone must be only digits")
    .required("Phone number is required"),
  Message: Yup.string().required("Message is required"),
});

const Earning = () => {
  const [faqs, setFaqs] = useState([]);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const formRef = useRef(null);
  const faqRef = useRef(null);

  // ✅ Submit Handler (used for both forms)
  const handleSubmit = async (values, { resetForm }) => {
    setIsFormLoading(true);
    try {
      const result = await AddAgencyInq(values);
      if (result.status === true) {
        resetForm();
      }
    } catch (err) {
      console.error("Submit error:", err);
    } finally {
      setIsFormLoading(false);
    }
  };

  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToFaq = () => {
    faqRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const res = await getAllFAQ();
        if (res) {
          setFaqs(res.responseData);
        }
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      }
    };
    fetchFaqs();
  }, []);

  return (
    <div className="bg-[#fff]">
      {isFormLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <PuffLoader color={"#83442B"} size={60} />
        </div>
      )}

      {/* Header */}
      <div
        style={{ backgroundImage: `url(${partnerbg})` }}
        className="bg-cover bg-center md:pb-20 pt-8 pb-12 text-center text-white relative"
      >
        {/* Logo */}
        <div className="container mx-auto mb-10 px-5 md:px-3 flex justify-between">
          <Link to={"/"}>
            <img src={logo} alt="Rotiey Logo" className="h-8 md:h-10" />
          </Link>
          <button onClick={scrollToFaq} className="cursor-pointer">
            Faqs
          </button>
        </div>


        <h1 className="text-3xl md:text-5xl font-semibold mb-4">
          Earn with the best
        </h1>
        <p className=" md:text-3xl text-xl mb-4">
          Deliver with Rotiey and get more opportunities to earn.
        </p>
        <h1 className="text-3xl md:text-5xl font-semibold mb-4 px-3">
          Register your Agency and grow your business
        </h1>
        <button
          onClick={scrollToForm}
          className="border mt-5 border-white text-white px-4 py-2 rounded-md hover:bg-theme2 font-medium"
        >
          Register
        </button>
      </div>

      {/* Steps + Documents */}
      <section className="container mx-auto px-4 py-12 grid md:grid-cols-2 gap-8">
        {/* Steps */}
        <div className="bg-gray-100 rounded-2xl p-6 shadow-sm">
          <h3 className="text-base font-semibold text-gray-500">
            In just 3 easy steps
          </h3>
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
            Get Started with Us Today
          </h2>

          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-theme2"></span>
              <div>
                <p className="text-sm text-gray-500">Step 1</p>
                <p className="font-semibold text-gray-700">
                  Install the Rotiey Owner App
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-theme2"></span>
              <div>
                <p className="text-sm text-gray-500">Step 2</p>
                <p className="font-semibold text-gray-700">
                  Login/Register using your phone number
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-theme2"></span>
              <div>
                <p className="text-sm text-gray-500">Step 3</p>
                <p className="font-semibold text-gray-700">Enter details</p>
              </div>
            </div>
          </div>
        </div>

        {/* Documents */}
        <div className="relative bg-gradient-to-b from-purple-50 to-white rounded-2xl p-6 border shadow-sm">
          <h3 className="font-semibold text-gray-700">
            For an easy form filling process,
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            you can keep these documents handy.
          </p>
          <hr className="mb-4" />
          <ul className="space-y-2 font-medium text-gray-700">
            <li>FSSAI License copy</li>
            <li>Your Restaurant menu</li>
            <li>Bank details</li>
            <li>GSTIN</li>
            <li>PAN card copy</li>
          </ul>
        </div>
      </section>

      {/* ✅ Full Contact Form */}
      <div ref={formRef} className="container mx-auto px-4 py-10">
        <h2 className="text-xl md:text-3xl text-darktext font-semibold mb-1 mt-2">
          Agency Registration Form
        </h2>
        <p className="text-contentcolor mb-6">
          Collaborate with us for mutual growth and success. Let's achieve more, together.
        </p>
        <div className="bg-white border rounded-md shadow-sm p-6">
          <Formik
            initialValues={{
              CompanyName: "",
              Name: "",
              CCODE: "",
              Email: "",
              Phone: "",
              Message: "",
            }}
            validationSchema={AgencySchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, handleReset }) => (
              <Form className="space-y-4">
                <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
                  <div>
                    <label>Company Name</label>
                    <Field
                      type="text"
                      name="CompanyName"
                      placeholder="Enter your Company Name"
                      className="w-full border px-4 py-3 mt-1.5 rounded-md"
                    />
                    <ErrorMessage
                      name="CompanyName"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                  <div>
                    <label>Name</label>
                    <Field
                      type="text"
                      name="Name"
                      placeholder="Enter your name"
                      className="w-full border px-4 py-3 mt-1.5 rounded-md"
                    />
                    <ErrorMessage
                      name="Name"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                  <div>
                    <label>Email</label>
                    <Field
                      type="email"
                      name="Email"
                      placeholder="Enter your email"
                      className="w-full border px-4 py-3 mt-1.5 rounded-md"
                    />
                    <ErrorMessage
                      name="Email"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                  <div>
                    <label>Phone Number</label>
                    <PhoneInput
                      country={"in"}
                      value={values.CCODE + values.Phone}
                      onChange={(value, countryData) => {
                        setFieldValue("CCODE", "+" + countryData.dialCode);
                        setFieldValue(
                          "Phone",
                          value.replace(countryData.dialCode, "")
                        );
                      }}
                      inputClass="!w-full !mt-1.5 !py-3 !border !rounded-md"
                    />
                    <ErrorMessage
                      name="Phone"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label>Message</label>
                  <Field
                    as="textarea"
                    rows="4"
                    name="Message"
                    placeholder="Hi there, I would like to..."
                    className="w-full border rounded-md px-3 py-3"
                  />
                  <ErrorMessage
                    name="Message"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-6 py-2 rounded-full bg-gray-100 text-gray-600"
                  >
                    CANCEL
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 rounded-full bg-theme2 hover:bg-black transition text-white"
                  >
                    SUBMIT
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Why Partner Section */}
      <section className="bg-gray-100 py-12">
        <div className="container mx-auto px-4 hidden md:block">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex justify-center items-center">
                <div className="flex flex-col items-center text-center p-6">
                  <div className="border border-gray-300 bg-white rounded-lg mb-3 p-2">
                    <img className="w-12" src={feature.icon} alt={feature.alt} />
                  </div>
                  <h5 className="text-lg font-semibold">{feature.title}</h5>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <div ref={faqRef} className="py-12">
        <h2 className="md:text-4xl text-2xl font-semibold mb-6 text-center">
          Frequently Asked Questions
        </h2>
        <div className="container mx-auto px-4 grid md:grid-cols-3 md:gap-10 items-center">
          <div className="flex justify-center md:col-span-1">
            <img src={faqimage} alt="FAQ illustration" className="w-full md:w-80" />
          </div>
          <div className="md:col-span-2">
            {faqs.length > 0 ? (
              faqs.map((faq, index) => (
                <div key={faq.Id} className="border rounded-md bg-white shadow-sm mb-2">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="flex justify-between items-center w-full px-4 py-4 text-lg font-medium text-left"
                  >
                    {faq.Title}
                    {activeIndex === index ? (
                      <FaChevronUp className="text-contentcolor" />
                    ) : (
                      <FaChevronDown className="text-contentcolor" />
                    )}
                  </button>
                  {activeIndex === index && (
                    <div className="px-4 pb-4 text-gray-600">{faq.Content}</div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">No FAQs available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Earning;
